import { useEffect, useState } from 'react'

import FileDropzone from 'admin/FileDropzone'
import ConfirmModal from 'common/ConfirmModal'
import { FilledYellow } from 'css/Buttons'
import { getBrochurePreSignedUrl } from '../../reports/documentsapi'
import { uploadBrochure as uploadBrochureFile } from '../mggadminutil'
import css from '../sass/upload.documents.module.scss'

const BrochureUploadButton = ({ year, type, onUploadSuccess, className }) => {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const [brochureExists, setBrochureExists] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')

  useEffect(() => {
    let isCurrentRequest = true

    setBrochureExists(false)
    setUploadError('')
    setUploadSuccess('')
    getBrochurePreSignedUrl(type, year)
      .then(response => {
        if (isCurrentRequest)
          setBrochureExists(Boolean(response?.presignedUrl))
      })
      .catch(() => {
        if (isCurrentRequest)
          setBrochureExists(false)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [type, year])

  useEffect(() => {
    setUploadError('')
  }, [selectedFiles])

  const resetModal = () => {
    setShowUploadModal(false)
    setSelectedFiles([])
    setDropzoneKey(key => key + 1)
  }

  const closeModal = () => {
    if (isUploading) return
    resetModal()
  }

  const uploadBrochure = async () => {
    if (selectedFiles.length === 0 || isUploading) return

    const file = selectedFiles[0]
    if (!/\.pdf$/i.test(file.name)) {
      setUploadError('The brochure must be a PDF file.')
      return
    }

    setIsUploading(true)
    setUploadError('')
    setUploadSuccess('')

    try {
      const response = await uploadBrochureFile(file, type, year)
      if (!response?.success) {
        const responseError = typeof response?.error === 'string'
          ? response.error
          : response?.error?.message || response?.message

        setUploadError(responseError || 'There was an error uploading the brochure.')
        return
      }

      setBrochureExists(true)
      setUploadSuccess(`${year} ${type} brochure uploaded successfully.`)
      if (onUploadSuccess)
        onUploadSuccess({year, type})

      resetModal()
    }
    catch (error) {
      setUploadError(error.message || 'There was an error uploading the brochure.')
    }
    finally {
      setIsUploading(false)
    }
  }

  return (
    <>
      <FilledYellow
        type="button"
        isSmall
        height={2.75}
        onClick={() => {
          setUploadError('')
          setUploadSuccess('')
          setShowUploadModal(true)
        }}
        className={`${css.reportUploadControlButton}${className ? ` ${className}` : ''}`}
      >
        {brochureExists ? 'RE-UPLOAD' : 'Upload'} {year} {type} Brochure
      </FilledYellow>
      {uploadSuccess && <span className={css.brochureUploadSuccess}>{uploadSuccess}</span>}

      {showUploadModal &&
        <ConfirmModal
          onConfirm={uploadBrochure}
          onCancel={closeModal}
          cancelText="CANCEL"
          confirmText={isUploading ? 'UPLOADING...' : 'UPLOAD'}
          confirmDisabled={selectedFiles.length === 0 || isUploading}
        >
          <div className={css.brochureUploadModal}>
            <h3>Upload {year} {type} Brochure</h3>
            <FileDropzone
              key={`brochure-dropzone-${dropzoneKey}`}
              dropzoneText={`Drag and drop the ${year} ${type} Brochure here`}
              setSelectedFiles={setSelectedFiles}
              multiple={false}
            />
            {uploadError && <div className={css.brochureUploadError}>{uploadError}</div>}
          </div>
        </ConfirmModal>}
    </>
  )
}

export default BrochureUploadButton
